import * as React from "react";
import {
    BasicFilter,
    FilterMetadata,
    FilterOperators,
    FilterTypes,
} from "~/modules/filters/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical } from "@fortawesome/pro-solid-svg-icons";
import { faClone, faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import { CSS } from "@dnd-kit/utilities";
import { OperatorSelect } from "./OperatorSelect";
import { StringValueInput } from "./inputs/StringValueInput";
import { getModifiedDragAndDropStyles } from "./getModifiedDragAndDropStyles";
import { EditorFilter } from "./types";
import { DateInput } from "~/components/DateInput";
import {
    FloatValueInput,
    PositiveFloatValueInput,
} from "./inputs/FloatValueInput";
import { PercentValueInput } from "./inputs/PercentValueInput";
import { SEARCH_FILTER_METADATA } from "../consts";
import { Tooltip } from "~/components/Tooltip";
import {
    IntegerValueInput,
    PositiveIntegerValueInput,
} from "./inputs/IntegerValueInput";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { useMemo } from "react";
import { SearchValueInput } from "./inputs/SearchValueInput";
import { getFirstMessage } from "./FilterMessageDisplay";
import { cn } from "~/lib/utils";
import {
    trackFilterFormChangeField,
    trackFilterFormChangeOperator,
    trackFilterFormChangeValue,
    trackFilterFormClickClone,
    trackFilterFormClickDelete,
} from "../analytics";
import { Combobox } from "~/components/ComboboxV2";
import { BooleanValueInput } from "./inputs/BooleanValueInput";
import { FieldSelectPanel } from "./FieldSelectPanel";
import { Modal } from "~/components/Modal";
import { ChevronDownIcon } from "lucide-react";

// // // //

function useCombinedRefs<T>(...refs: ((node: T) => void)[]): (node: T) => void {
    return useMemo(
        () => (node: T) => {
            refs.forEach((ref) => ref(node));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        refs,
    );
}

// // // //

function ActionButton(props: {
    type: "delete" | "clone";
    onClick: () => void;
}) {
    const { type, onClick } = props;

    const tooltipContent = type === "delete" ? "Remove Filter" : "Clone Filter";
    const icon = type === "delete" ? faTrashAlt : faClone;
    const iconClassName =
        type === "delete" ? "hover:text-rose-700" : "hover:text-emerald-700";

    return (
        <Tooltip
            placement="top"
            tooltipContent={
                <div className="px-4 flex items-center gap-2">
                    <p className="py-2 font-light max-w-160">
                        {tooltipContent}
                    </p>
                </div>
            }
        >
            {({ setTriggerRef }) => (
                <button
                    ref={setTriggerRef}
                    className={`px-3 py-3.5 transition-colors text-muted-foreground ${iconClassName}`}
                    onClick={() => {
                        onClick();
                    }}
                >
                    <FontAwesomeIcon
                        icon={icon}
                        className="h-4 mb-0.5 inline"
                    />
                    <span className="sr-only">{tooltipContent}</span>
                </button>
            )}
        </Tooltip>
    );
}

// // // //

// Operators that don't have a value input.
const NULL_OPERATORS = [FilterOperators.IsNull, FilterOperators.IsNotNull];

export function FilterForm(props: {
    filter: EditorFilter;
    onChange: (updatedFilter: EditorFilter) => void;
    onRemove: () => void;
    onClone: () => void;
    filterMetadata: FilterMetadata[];
    index: number;
    parent: EditorFilter;
}) {
    const {
        filter,
        index,
        parent,
        onChange,
        onRemove,
        onClone,
        filterMetadata,
    } = props;

    const {
        active,
        attributes,
        isDragging,
        listeners,
        setNodeRef,
        transform,
        activatorEvent,
        // transition,
        // activatorEvent,
    } = useDraggable({
        id: filter.id,
        data: {
            ...filter.state,
            parentID: parent.id,
            index,
        },
    });

    const {
        // rect,
        // node,
        over,
        isOver,
        setNodeRef: setDroppableNodeRef,
    } = useDroppable({
        id: filter.id,
        data: {
            ...filter.state,
            parentID: parent.id,
            index,
        },
        // disabled: disabled.droppable,
        disabled: false,
        // resizeObserverConfig: {
        //     updateMeasurementsFor: itemsAfterCurrentSortable,
        // },
    });

    const combinedRef = useCombinedRefs(setDroppableNodeRef, setNodeRef);

    const [fieldPanelOpen, setFieldPanelOpen] = React.useState(false);

    const style: React.CSSProperties = {
        opacity: isDragging ? 0.8 : undefined,
        transform: CSS.Transform.toString(transform),
        // transition,
    };

    if (isOver) {
        // console.log("IS OVER");
        // console.log(over);
        // console.log(over?.id === filter.id);
        // console.log(active?.id === filter.id);
        if (active?.id !== filter.id) {
            // if (transform?.x)
            // console.log(index);
            // console.log(parent.nested.length);
            // console.log(active?.data.current.index);

            // Check if in the same parent
            // console.log(transform?.y);
            // @ts-ignore
            // console.log(activatorEvent?.offsetY || "");

            // @ts-ignore
            const offsetY = activatorEvent?.offsetY || 0;

            // Shift the hovered row by the dragged row's actual height so the
            // gap that opens up lines up with the item being dropped in (the
            // previous hard-coded 50px didn't match the real row height).
            const dragHeight = active?.rect.current.initial?.height ?? 50;

            if (offsetY < 20) {
                style.transform = `translateY(-${dragHeight}px)`;
            } else {
                style.transform = `translateY(${dragHeight}px)`;
            }

            style.transition = CSS.Transition.toString({
                duration: 100,
                easing: "ease",
                property: "transform",
            });
        }
    }

    const selectedFilterMetadata =
        filterMetadata.find((each) => each.field === filter.state.field) ||
        filterMetadata[0];

    // The search filter has no operator. When it's hidden, the value column
    // grows to span the operator + value space so the field column keeps the
    // same width as every other row and the columns stay aligned.
    const showOperator =
        selectedFilterMetadata.field !== SEARCH_FILTER_METADATA.field;

    // Hide the value input for the isNull and isNotNull operators.
    const hideValueInput = NULL_OPERATORS.includes(
        filter.state.operator as FilterOperators,
    );

    // Get EditorFilterMessage
    const message = getFirstMessage({ filter });
    const hasErrorMessage = message?.variant === "error";
    const hasWarningMessage = message?.variant === "warning";

    const comboboxOptionsSize = selectedFilterMetadata.selectOptions?.some(
        (each) => each.content.description,
    )
        ? "md"
        : "lg";

    return (
        <div
            ref={combinedRef}
            // TODO - fix style here
            style={getModifiedDragAndDropStyles(style, { isDragging })}
            // style={style}
            // style={
            //     {
            //         ...style,
            //         "--translate-x": `${transform?.x ?? 0}px`,
            //         "--translate-y": `${transform?.y ?? 0}px`,
            //     } as React.CSSProperties
            // }
            className={cn(
                "flex p-0 items-center justify-between border rounded-lg select-none",
                {
                    "border-red-200 bg-red-50/50": hasErrorMessage,
                    "border-orange-200 bg-orange-50/50":
                        !hasErrorMessage && hasWarningMessage,
                    "bg-accent": !hasErrorMessage && !hasWarningMessage,
                },
            )}
        >
            <div
                {...listeners}
                {...attributes}
                className="cursor-grab transition-colors text-muted-foreground hover:text-emerald-700 pl-3 pr-0.5 py-3.5"
            >
                <FontAwesomeIcon
                    icon={faGripVertical}
                    className="h-3.5 mb-0.5 inline"
                />
            </div>

            {/* ENHANCEMENT - find a way to integrate FilterMessages into this component */}
            {/* {message && (
                <div className="px-3 py-3.5 transition-colors text-slate-400 hover:text-emerald-700">
                    <pre>
                        <FilterMessagesDisplay
                            filter={filter}
                            onRemove={onRemove}
                            onChange={onChange}
                        />
                    </pre>
                </div>
            )} */}

            <div className="flex flex-wrap h-full w-48 items-center grow gap-2 px-2 py-2">
                <div className="flex flex-col grow basis-[180px] min-w-[180px]">
                    <button
                        type="button"
                        onClick={() => setFieldPanelOpen(true)}
                        className={cn(
                            "flex h-10 w-full items-center justify-between gap-2 rounded border border-input bg-background px-3.5 py-2 text-base transition-colors",
                            "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            fieldPanelOpen && "border-ring ring-2 ring-ring/50",
                        )}
                    >
                        <span className="truncate text-foreground">
                            {selectedFilterMetadata.content.label}
                        </span>
                        <ChevronDownIcon className="size-5 shrink-0 text-muted-foreground" />
                    </button>
                    {/* size="sm" applies no width constraint, so the modal
                        shrink-wraps to the panel's own width */}
                    <Modal
                        show={fieldPanelOpen}
                        size="sm"
                        allowScroll={true}
                        onHide={() => setFieldPanelOpen(false)}
                    >
                        {/* The panel's input swallows the first Escape to clear
                            an active search; a bubbled Escape closes the modal */}
                        <div
                            onKeyDown={(e) => {
                                if (e.key === "Escape") {
                                    setFieldPanelOpen(false);
                                }
                            }}
                        >
                            <FieldSelectPanel
                                value={filter.state.field as string}
                                compact={false}
                                filterMetadata={filterMetadata}
                                onChange={(updatedField) => {
                                    // Set updated field + operator + value
                                    const filterMeta = filterMetadata.find(
                                        (each) => {
                                            return each.field === updatedField;
                                        },
                                    );

                                    const defaultOperator =
                                        filterMeta?.defaultOperator ||
                                        filter.state.operator;
                                    const defaultValue =
                                        filterMeta?.defaultValue;

                                    trackFilterFormChangeField({
                                        prevField: filter.state.field || "",
                                        nextField: updatedField || "",
                                    });

                                    onChange({
                                        ...filter,
                                        state: {
                                            field: updatedField,
                                            operator: defaultOperator,
                                            value: defaultValue,
                                        } as BasicFilter,
                                    });

                                    setFieldPanelOpen(false);
                                }}
                            />
                        </div>
                    </Modal>
                </div>

                {showOperator && (
                    <div className="flex flex-col grow basis-[140px] min-w-[140px]">
                        <OperatorSelect
                            value={filter.state.operator}
                            operators={selectedFilterMetadata.allowedOperators}
                            onChange={(updatedOperator) => {
                                trackFilterFormChangeOperator({
                                    prevOperator: filter.state.operator,
                                    nextOperator: updatedOperator,
                                });

                                // Check if the operator is a null operator.
                                const isNullOperator = NULL_OPERATORS.includes(
                                    updatedOperator as FilterOperators,
                                );

                                // If the operator is a null operator, set the value to null.
                                const value = isNullOperator
                                    ? null
                                    : filter.state.value;

                                // Set the updated operator and value.
                                onChange({
                                    ...filter,
                                    state: {
                                        ...filter.state,
                                        operator: updatedOperator,
                                        value,
                                    } as BasicFilter,
                                });
                            }}
                        />
                    </div>
                )}

                {!hideValueInput && (
                    <div
                        className={cn(
                            "flex flex-col min-w-[200px]",
                            // When the operator is hidden (search filter), span the
                            // operator + value space: basis = 140 (op) + 8 (gap-2) +
                            // 200 (value) and grow-2 so the field column stays aligned.
                            showOperator
                                ? "grow basis-[200px]"
                                : "grow-2 basis-[348px]",
                        )}
                    >
                        {selectedFilterMetadata.type === FilterTypes.String && (
                            <StringValueInput
                                value={filter.state.value as string}
                                filterMetadata={selectedFilterMetadata}
                                onChange={(updatedValue) => {
                                    trackFilterFormChangeValue({
                                        prevValue: filter.state.value,
                                        nextValue: updatedValue,
                                    });
                                    onChange({
                                        ...filter,
                                        state: {
                                            ...filter.state,
                                            value: updatedValue,
                                        } as BasicFilter,
                                    });
                                }}
                            />
                        )}

                        {selectedFilterMetadata.type === FilterTypes.Search && (
                            <SearchValueInput
                                value={filter.state.value as string}
                                filterMeta={selectedFilterMetadata}
                                onChange={(updatedValue) => {
                                    trackFilterFormChangeValue({
                                        prevValue: filter.state.value,
                                        nextValue: updatedValue,
                                    });
                                    onChange({
                                        ...filter,
                                        state: {
                                            ...filter.state,
                                            value: updatedValue,
                                        } as BasicFilter,
                                    });
                                }}
                            />
                        )}

                        {selectedFilterMetadata.type === FilterTypes.Date && (
                            <DateInput
                                value={filter.state.value as string}
                                onChange={(updatedValue) => {
                                    trackFilterFormChangeValue({
                                        prevValue: filter.state.value,
                                        nextValue: updatedValue,
                                    });
                                    onChange({
                                        ...filter,
                                        state: {
                                            ...filter.state,
                                            value: updatedValue,
                                        } as BasicFilter,
                                    });
                                }}
                            />
                        )}

                        {selectedFilterMetadata.type ===
                            FilterTypes.StringSingleSelect && (
                            <Combobox<string>
                                size="lg"
                                itemSize={comboboxOptionsSize}
                                value={filter.state.value as string}
                                options={(
                                    selectedFilterMetadata.selectOptions || []
                                ).map((each) => ({
                                    value: each.value as string,
                                    label: each.content.label,
                                    description: each.content.description,
                                    icon_url: each.content.icon,
                                }))}
                                onChange={(updatedValue) => {
                                    trackFilterFormChangeValue({
                                        prevValue: filter.state.value,
                                        nextValue: updatedValue,
                                    });
                                    onChange({
                                        ...filter,
                                        state: {
                                            ...filter.state,
                                            value: updatedValue,
                                        } as BasicFilter,
                                    });
                                }}
                                disabled={false}
                            />
                        )}

                        {selectedFilterMetadata.type ===
                            FilterTypes.Integer && (
                            <IntegerValueInput
                                filterMeta={selectedFilterMetadata}
                                value={filter.state.value as number}
                                onChange={(updatedValue) => {
                                    trackFilterFormChangeValue({
                                        prevValue: filter.state.value,
                                        nextValue: updatedValue,
                                    });
                                    onChange({
                                        ...filter,
                                        state: {
                                            ...filter.state,
                                            value: updatedValue,
                                        } as BasicFilter,
                                    });
                                }}
                            />
                        )}

                        {selectedFilterMetadata.type ===
                            FilterTypes.PositiveInteger && (
                            <PositiveIntegerValueInput
                                filterMeta={selectedFilterMetadata}
                                value={filter.state.value as number}
                                onChange={(updatedValue) => {
                                    trackFilterFormChangeValue({
                                        prevValue: filter.state.value,
                                        nextValue: updatedValue,
                                    });
                                    onChange({
                                        ...filter,
                                        state: {
                                            ...filter.state,
                                            value: updatedValue,
                                        } as BasicFilter,
                                    });
                                }}
                            />
                        )}

                        {selectedFilterMetadata.type === FilterTypes.Float && (
                            <FloatValueInput
                                filterMeta={selectedFilterMetadata}
                                value={filter.state.value as number}
                                onChange={(updatedValue) => {
                                    onChange({
                                        ...filter,
                                        state: {
                                            ...filter.state,
                                            value: updatedValue,
                                        } as BasicFilter,
                                    });
                                }}
                            />
                        )}

                        {selectedFilterMetadata.type ===
                            FilterTypes.PositiveFloat && (
                            <PositiveFloatValueInput
                                filterMeta={selectedFilterMetadata}
                                value={filter.state.value as number}
                                onChange={(updatedValue) => {
                                    trackFilterFormChangeValue({
                                        prevValue: filter.state.value,
                                        nextValue: updatedValue,
                                    });
                                    onChange({
                                        ...filter,
                                        state: {
                                            ...filter.state,
                                            value: updatedValue,
                                        } as BasicFilter,
                                    });
                                }}
                            />
                        )}

                        {selectedFilterMetadata.type === FilterTypes.Dollar && (
                            <PositiveFloatValueInput
                                filterMeta={selectedFilterMetadata}
                                value={filter.state.value as number}
                                onChange={(updatedValue) => {
                                    trackFilterFormChangeValue({
                                        prevValue: filter.state.value,
                                        nextValue: updatedValue,
                                    });
                                    onChange({
                                        ...filter,
                                        state: {
                                            ...filter.state,
                                            value: updatedValue,
                                        } as BasicFilter,
                                    });
                                }}
                            />
                        )}

                        {selectedFilterMetadata.type ===
                            FilterTypes.Percent && (
                            <PercentValueInput
                                filterMeta={selectedFilterMetadata}
                                value={filter.state.value as number}
                                onChange={(updatedValue) => {
                                    trackFilterFormChangeValue({
                                        prevValue: filter.state.value,
                                        nextValue: updatedValue,
                                    });
                                    onChange({
                                        ...filter,
                                        state: {
                                            ...filter.state,
                                            value: updatedValue,
                                        } as BasicFilter,
                                    });
                                }}
                            />
                        )}

                        {selectedFilterMetadata.type ===
                            FilterTypes.Boolean && (
                            <BooleanValueInput
                                value={
                                    typeof filter.state.value === "boolean"
                                        ? filter.state.value
                                        : false
                                }
                                onChange={(updatedValue: boolean) => {
                                    trackFilterFormChangeValue({
                                        prevValue: filter.state.value,
                                        nextValue: updatedValue,
                                    });
                                    onChange({
                                        ...filter,
                                        state: {
                                            ...filter.state,
                                            value: updatedValue,
                                        } as BasicFilter,
                                    });
                                }}
                            />
                        )}
                    </div>
                )}
            </div>

            <ActionButton
                type="clone"
                onClick={() => {
                    trackFilterFormClickClone({
                        field: filter.state.field || "",
                        value: filter.state.value,
                        operator: filter.state.operator,
                    });

                    onClone();
                }}
            />

            <ActionButton
                type="delete"
                onClick={() => {
                    trackFilterFormClickDelete({
                        field: filter.state.field || "",
                        value: filter.state.value,
                        operator: filter.state.operator,
                    });

                    onRemove();
                }}
            />
        </div>
    );
}
